import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  INVITABLE_ROLES,
  InviteUserRequestSchema,
  USER_STATUSES,
  type InviteUserRequest,
  type RosterResponse,
  type UserSummary,
  type UsersListResponse,
} from '@besideu/shared/users';
import type { Role } from '@besideu/shared/roles';
import { api, ApiError } from '@/lib/api.ts';
import { useSession } from '@/lib/auth.ts';
import { useDebouncedValue } from '@/lib/use-debounced-value.ts';
import { Button } from '@/components/ui/Button.tsx';
import { Field, Select, TextInput } from '@/components/ui/Field.tsx';
import { ManagerSelect } from '@/components/ManagerSelect.tsx';

const PAGE_SIZE = 50;

export function HrMembers() {
  const queryClient = useQueryClient();
  const session = useSession('hr-shell');
  const isAdmin = session.role === 'HR_Admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get('employee');
  // PO 2026-08-07 ("can we have the ability to delete people... just so we
  // can clean it up"): rather than a new delete affordance, the default view
  // now hides Opted_Out (and Pending) members behind this same filter — the
  // list HR sees day-to-day stays limited to their active team. Deactivated
  // people aren't lost: `all`/`Opted_Out` in the URL still surfaces them.
  // `null` (no `status` param at all) is the untouched default and reads as
  // "Active"; `all` is a distinct sentinel for "no filter" so picking it
  // doesn't collapse back to the default on the next render.
  const statusParam = searchParams.get('status');
  const status = statusParam === 'all' ? '' : (statusParam ?? 'Active');
  const role = searchParams.get('role') ?? '';
  const qParam = searchParams.get('q') ?? '';
  const page = Number(searchParams.get('page') ?? '0');

  const [qInput, setQInput] = useState(qParam);
  const debouncedQ = useDebouncedValue(qInput, 300);
  // Tracks the last value THIS component pushed into the URL, so the resync
  // effect below can tell "the URL changed because we just typed" (no-op)
  // apart from "the URL changed from elsewhere" (browser back/forward,
  // another setSearchParams caller) — which must resync qInput/the visible
  // search box, or it silently disagrees with the URL and the results.
  const lastPushedQ = useRef(qParam);

  // Push the debounced search text into the URL (and reset to page 0) once
  // it settles — this is what actually drives the server-side fetch below.
  useEffect(() => {
    if (debouncedQ === qParam) return;
    lastPushedQ.current = debouncedQ;
    const sp = new URLSearchParams(searchParams);
    if (debouncedQ) sp.set('q', debouncedQ);
    else sp.delete('q');
    sp.delete('page');
    setSearchParams(sp, { replace: true });
    // Deliberately narrow deps: only react to the debounced value settling.
    // Including searchParams/qParam would loop against our own
    // setSearchParams call above.
  }, [debouncedQ]);

  // Resync the visible search box when `q` changes from a source OTHER than
  // the debounced push above (browser back/forward, or any other
  // setSearchParams caller) — otherwise the box can keep showing stale text
  // after the URL/results have already changed underneath it.
  useEffect(() => {
    if (qParam === lastPushedQ.current) return;
    lastPushedQ.current = qParam;
    setQInput(qParam);
  }, [qParam]);

  function setStatusFilter(next: string) {
    const sp = new URLSearchParams(searchParams);
    // 'Active' is the default and stays out of the URL so the plain
    // `/hr/members` link keeps that behaviour; every other choice, including
    // 'all', is recorded explicitly.
    if (next === 'Active') sp.delete('status');
    else sp.set('status', next);
    sp.delete('page');
    setSearchParams(sp, { replace: true });
  }
  function setRoleFilter(next: string) {
    const sp = new URLSearchParams(searchParams);
    if (!next) sp.delete('role');
    else sp.set('role', next);
    sp.delete('page');
    setSearchParams(sp, { replace: true });
  }
  function setPage(next: number) {
    const sp = new URLSearchParams(searchParams);
    if (next === 0) sp.delete('page');
    else sp.set('page', String(next));
    setSearchParams(sp, { replace: true });
  }

  const members = useQuery({
    queryKey: ['users', { q: qParam, status, role, page }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (qParam) params.set('q', qParam);
      if (status) params.set('status', status);
      if (role) params.set('role', role);
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(page * PAGE_SIZE));
      return api.get<UsersListResponse>(`/api/users?${params}`);
    },
    placeholderData: (prev) => prev,
  });
  const total = members.data?.total ?? 0;
  // The default Active-only status doesn't count as a user-applied filter
  // for this message — a brand-new org with zero members should still see
  // "No members yet", not "No members match the current filters".
  const hasFilters =
    qParam !== '' || role !== '' || (statusParam !== null && statusParam !== 'Active');

  const invite = useMutation({
    mutationFn: (input: InviteUserRequest) =>
      api.post<{ user: UserSummary }>('/api/users/invites', input),
    // Also invalidates the dedicated ['users', 'roster', 'Manager'] query
    // below — TanStack Query treats ['users'] as a prefix match against any
    // ['users', ...] key, so a single invalidate covers both.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  // Slice 36 — re-arm the magic-link token for a user who lost the
  // original email. The route mints a fresh D1 row in `magic_link_tokens`
  // and queues a magic-link email — independent of the original invite
  // (so 5 resends → 5 distinct, single-use links). Per-row state lives
  // in a useState map so multiple rows can be in-flight at once without
  // the loading spinner propagating across the table.
  const [resendPending, setResendPending] = useState<Record<string, boolean>>({});
  const [resendBanner, setResendBanner] = useState<
    | { kind: 'ok'; ttlMinutes: number; recipient: string }
    | { kind: 'err'; message: string; retryAfterMs?: number }
    | null
  >(null);
  const resend = useMutation({
    mutationFn: (userId: string) => api.post<{ ok: true }>(`/api/users/${userId}/resend-invite`),
    onMutate: (userId) => setResendPending((m) => ({ ...m, [userId]: true })),
    onSettled: (_data, _err, userId) =>
      setResendPending((m) => {
        const { [userId]: _omit, ...rest } = m;
        return rest;
      }),
    onSuccess: (_data, userId) => {
      // Look the user up from the cached members query so we can show
      // the right TTL in the success banner (2h for HR/Manager, 30d for
      // Employee). Pulls from the same data the table renders; if the
      // query is still loading the banner falls back to the 2h default
      // (the conservative choice for any non-Employee user).
      const user = members.data?.users.find((u) => u.id === userId);
      const isEmployee = user?.role === 'Employee';
      const ttlMinutes = isEmployee ? 60 * 24 * 30 : 60 * 2;
      setResendBanner({
        kind: 'ok',
        ttlMinutes,
        recipient: user?.name ?? 'the user',
      });
    },
    onError: (err: unknown) => setResendBanner(translateResendError(err)),
  });

  // Auto-dismiss the banner after 5s — long enough to read, short
  // enough not to leak a stale state across unrelated actions. Manual
  // dismiss via the button still works (clears the timeout by
  // replacing the banner with `null`).
  useEffect(() => {
    if (!resendBanner) return;
    const t = setTimeout(() => setResendBanner(null), 5000);
    return () => clearTimeout(t);
  }, [resendBanner]);

  // Stable onResend reference for the table. `useCallback` keeps the
  // prop identity stable across parent renders so the table's row
  // children don't re-render on every banner state change (the
  // `resendPending` map still triggers per-row re-render, which is
  // the only re-render the table actually needs). Negligible for a
  // small HR_members list; matters for hundreds of users.
  const handleResend = useCallback(
    (m: UserSummary) => {
      setResendBanner(null);
      resend.mutate(m.id);
    },
    [resend],
  );

  // The write path for `user_manager_mappings` — previously nothing in the
  // app ever set this, so HR had no way to fix the "no manager mapping"
  // dead end that `/hr/cases/new` deep-links here for (see the
  // `highlightId` handling below). Per-row pending/error state mirrors
  // `resendPending` above rather than a shared banner, since this is a
  // per-row inline control, not a form submission.
  const [managerPending, setManagerPending] = useState<Record<string, boolean>>({});
  const [managerErrors, setManagerErrors] = useState<Record<string, string | null>>({});
  const setManager = useMutation({
    mutationFn: ({ employeeId, managerId }: { employeeId: string; managerId: string }) =>
      api.put<{ ok: true; managerId: string }>(`/api/users/${employeeId}/manager`, { managerId }),
    onMutate: ({ employeeId }) => {
      setManagerPending((m) => ({ ...m, [employeeId]: true }));
      setManagerErrors((m) => ({ ...m, [employeeId]: null }));
    },
    onSettled: (_data, _err, { employeeId }) =>
      setManagerPending((m) => {
        const { [employeeId]: _omit, ...rest } = m;
        return rest;
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
    onError: (err: unknown, { employeeId }) =>
      setManagerErrors((m) => ({ ...m, [employeeId]: translateManagerError(err) })),
  });
  const handleSetManager = useCallback(
    (employeeId: string, managerId: string) => setManager.mutate({ employeeId, managerId }),
    [setManager],
  );

  // Role change post-invite — previously fixed at invite time (deactivate +
  // re-invite was the only way to change it). Per-row pending/error state
  // mirrors `managerPending`/`managerErrors` above. Role change grants/
  // revokes access, so — unlike the plain-select manager reassignment —
  // this goes through a confirm() dialog, matching `handleToggleStatus`'s
  // posture for the other access-affecting action in this file. The
  // "takes effect at next sign-in" line is accurate, not just cautious:
  // `middleware/auth.ts` trusts `role` from the signed JWT and only
  // point-reads `status` per request, so an already-signed-in session
  // keeps its old role until it naturally re-mints.
  const [rolePending, setRolePending] = useState<Record<string, boolean>>({});
  const [roleErrors, setRoleErrors] = useState<Record<string, string | null>>({});
  const setRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: Role }) =>
      api.post<{ ok: true }>(`/api/users/${userId}/role`, { role }),
    onMutate: ({ userId }) => {
      setRolePending((m) => ({ ...m, [userId]: true }));
      setRoleErrors((m) => ({ ...m, [userId]: null }));
    },
    onSettled: (_data, _err, { userId }) =>
      setRolePending((m) => {
        const { [userId]: _omit, ...rest } = m;
        return rest;
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
    onError: (err: unknown, { userId }) =>
      setRoleErrors((m) => ({ ...m, [userId]: translateRoleError(err) })),
  });
  const handleSetRole = useCallback(
    (m: UserSummary, role: Role) => {
      const confirmed = window.confirm(
        `Change ${m.name}'s role from ${m.role.replace('_', ' ')} to ${role.replace('_', ' ')}? ` +
          `This takes effect the next time they sign in.`,
      );
      if (!confirmed) return;
      setRoleErrors((e) => ({ ...e, [m.id]: null }));
      setRole.mutate({ userId: m.id, role });
    },
    [setRole],
  );

  // Manager roster — SEPARATE, dedicated query from `members` above, not
  // derived from it: `members` is now paginated (default 50/page), so an
  // org with >50 members could have Managers that never load into
  // `members.data`. Unpaginated + ALL statuses (not just Active) via the
  // dedicated roster endpoint, because this list serves two different jobs:
  // `managers` (Active-only, below) is the SELECTABLE options for
  // assignment; `managerRoster` (unfiltered) is what `ManagerCell` needs to
  // resolve the NAME of an employee's already-assigned manager even if that
  // manager has since been deactivated (`deactivateUser` never touches
  // `user_manager_mappings`) — `ManagerSelect`'s fallback-option guard
  // depends on that lookup succeeding, or it silently shows the wrong
  // manager (see ManagerSelect.tsx's header comment).
  const managerRosterQuery = useQuery({
    queryKey: ['users', 'roster', 'Manager'],
    queryFn: () => api.get<RosterResponse>('/api/users/roster?role=Manager'),
  });
  const managerRoster = managerRosterQuery.data?.users ?? [];
  const managers = managerRoster.filter((u) => u.status === 'Active');

  // Offboarding — the write path `Opted_Out` never had. A single action
  // toggles Active<->Opted_Out depending on the row's current status;
  // `requireAuth` (middleware/auth.ts) point-reads `users.status` per
  // request, so deactivating someone cuts off their live session
  // immediately, not just future sign-ins.
  const [statusPending, setStatusPending] = useState<Record<string, boolean>>({});
  const [statusBanner, setStatusBanner] = useState<{ kind: 'ok' | 'err'; message: string } | null>(
    null,
  );
  const toggleStatus = useMutation({
    mutationFn: ({ userId, action }: { userId: string; action: 'deactivate' | 'reactivate' }) =>
      api.post<{ ok: true }>(`/api/users/${userId}/${action}`),
    onMutate: ({ userId }) => setStatusPending((m) => ({ ...m, [userId]: true })),
    onSettled: (_data, _err, { userId }) =>
      setStatusPending((m) => {
        const { [userId]: _omit, ...rest } = m;
        return rest;
      }),
    onSuccess: (_data, { userId, action }) => {
      const name = members.data?.users.find((u) => u.id === userId)?.name ?? 'The user';
      setStatusBanner({
        kind: 'ok',
        message:
          action === 'deactivate'
            ? `${name} has been deactivated and signed out.`
            : `${name} has been reactivated.`,
      });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: unknown) => setStatusBanner({ kind: 'err', message: translateStatusError(err) }),
  });

  useEffect(() => {
    if (!statusBanner) return;
    const t = setTimeout(() => setStatusBanner(null), 5000);
    return () => clearTimeout(t);
  }, [statusBanner]);

  const handleToggleStatus = useCallback(
    (m: UserSummary) => {
      const action = m.status === 'Opted_Out' ? 'reactivate' : 'deactivate';
      const confirmed = window.confirm(
        action === 'deactivate'
          ? `Deactivate ${m.name}? They will lose portal access immediately and drop off your default Members list. This can be reversed any time by filtering to "Opted Out" and reactivating them here.`
          : `Reactivate ${m.name}? They will be able to sign in again.`,
      );
      if (!confirmed) return;
      setStatusBanner(null);
      toggleStatus.mutate({ userId: m.id, action });
    },
    [toggleStatus],
  );

  // Self-service HR Safety Contact reassignment — DESIGN.md: "may be
  // reassigned post-signup either by HR_Admin within the tenant or by
  // BesideU Admin in support scenarios", gated on "the receiving user
  // completing the acknowledgement step" (lines 735/742) before the
  // reassignment takes effect. Two-step: nominate sets a pending row,
  // accept (by the nominee, self only) is the only thing that actually
  // moves `hrSafetyContactUserId`. Reuses `statusBanner` for feedback
  // rather than a fourth near-identical banner copy in this file.
  const [safetyContactPending, setSafetyContactPending] = useState<Record<string, boolean>>({});
  function setPendingKey(key: string, value: boolean) {
    setSafetyContactPending((m) => {
      if (!value) {
        const { [key]: _omit, ...rest } = m;
        return rest;
      }
      return { ...m, [key]: true };
    });
  }
  // Nominate is keyed by the target row's user id (multiple eligible rows
  // could be clicked in quick succession); accept/cancel are keyed by fixed
  // strings since at most one row ever shows either button at a time — an
  // org has at most one pending nomination.
  const nominateSafetyContact = useMutation({
    mutationFn: (userId: string) => api.post<{ ok: true }>(`/api/users/${userId}/safety-contact`),
    onMutate: (userId) => setPendingKey(userId, true),
    onSettled: (_data, _err, userId) => setPendingKey(userId, false),
    onSuccess: (_data, userId) => {
      const name = members.data?.users.find((u) => u.id === userId)?.name ?? 'The user';
      setStatusBanner({
        kind: 'ok',
        message: `${name} has been nominated as Safety Contact — pending their acceptance.`,
      });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: unknown) =>
      setStatusBanner({ kind: 'err', message: translateSafetyContactError(err) }),
  });
  const acceptSafetyContact = useMutation({
    mutationFn: () => api.post<{ ok: true }>('/api/users/safety-contact/accept'),
    onMutate: () => setPendingKey('accept', true),
    onSettled: () => setPendingKey('accept', false),
    onSuccess: () => {
      setStatusBanner({ kind: 'ok', message: "You're now the org's Safety Contact." });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: unknown) =>
      setStatusBanner({ kind: 'err', message: translateSafetyContactError(err) }),
  });
  const cancelSafetyContact = useMutation({
    mutationFn: () => api.post<{ ok: true }>('/api/users/safety-contact/cancel'),
    onMutate: () => setPendingKey('cancel', true),
    onSettled: () => setPendingKey('cancel', false),
    onSuccess: () => {
      setStatusBanner({ kind: 'ok', message: 'Nomination cancelled.' });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: unknown) =>
      setStatusBanner({ kind: 'err', message: translateSafetyContactError(err) }),
  });
  const handleNominateSafetyContact = useCallback(
    (m: UserSummary) => {
      // Best-effort: `members.data` is now just the current page, so the
      // current Safety Contact may not be on it (falls back to the generic
      // phrase below) — this is confirm-dialog copy, not access control, so
      // an occasional generic fallback is an acceptable trade for not adding
      // a third dedicated fetch here.
      const currentName =
        members.data?.users.find((u) => u.id === members.data.hrSafetyContactUserId)?.name ??
        'no one currently';
      const confirmed = window.confirm(
        `Nominate ${m.name} as the org's Safety Contact? They'll need to accept before red-flag crisis notifications are routed to them instead of ${currentName}.`,
      );
      if (!confirmed) return;
      setStatusBanner(null);
      nominateSafetyContact.mutate(m.id);
    },
    [nominateSafetyContact, members.data],
  );
  const handleAcceptSafetyContact = useCallback(() => {
    setStatusBanner(null);
    acceptSafetyContact.mutate();
  }, [acceptSafetyContact]);
  const handleCancelSafetyContact = useCallback(() => {
    setStatusBanner(null);
    cancelSafetyContact.mutate();
  }, [cancelSafetyContact]);

  return (
    <section className="space-y-10">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Members</h1>
        <p className="mt-2 text-muted-foreground">
          Invite teammates by email — they'll receive a one-time sign-in link. Deactivated members
          are hidden here by default — switch the status filter below to see them.
        </p>
      </header>

      <InviteForm
        pending={invite.isPending}
        error={errorMessage(invite.error)}
        onSubmit={(input) => invite.mutate(input)}
      />

      {resendBanner ? (
        // role="status" so the announcement fires on the next paint without
        // stealing focus. WCAG 2.1 AA 4.1.3. The auto-dismiss timer
        // (5s) lives in the parent useEffect above.
        <div
          role="status"
          aria-live="polite"
          className={
            resendBanner.kind === 'ok'
              ? 'rounded-md border border-traffic-green/40 bg-traffic-green/5 p-3 text-sm'
              : 'rounded-md border border-traffic-red/40 bg-traffic-red/5 p-3 text-sm'
          }
        >
          {resendBanner.kind === 'ok' ? (
            <span>
              Sign-in link re-sent to {resendBanner.recipient}. The new{' '}
              {resendBanner.ttlMinutes >= 60 * 24 ? '30-day' : '2-hour'} link is now in their inbox.
            </span>
          ) : (
            <ResendErrorMessage banner={resendBanner} />
          )}
          <button
            type="button"
            onClick={() => setResendBanner(null)}
            className="ml-3 text-xs text-muted-foreground underline underline-offset-2 hover:no-underline"
            aria-label="Dismiss"
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {statusBanner ? (
        <div
          role="status"
          aria-live="polite"
          className={
            statusBanner.kind === 'ok'
              ? 'rounded-md border border-traffic-green/40 bg-traffic-green/5 p-3 text-sm'
              : 'rounded-md border border-traffic-red/40 bg-traffic-red/5 p-3 text-sm'
          }
        >
          <span>{statusBanner.message}</span>
          <button
            type="button"
            onClick={() => setStatusBanner(null)}
            className="ml-3 text-xs text-muted-foreground underline underline-offset-2 hover:no-underline"
            aria-label="Dismiss"
          >
            Dismiss
          </button>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          placeholder="Search name or email"
          aria-label="Search name or email"
          className="min-w-0 flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none sm:max-w-xs"
        />
        <Select
          aria-label="Filter by status"
          value={statusParam ?? 'Active'}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="w-auto py-2 text-sm"
        >
          {USER_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.replace('_', ' ')}
            </option>
          ))}
          <option value="all">All statuses</option>
        </Select>
        <Select
          aria-label="Filter by role"
          value={role}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="w-auto py-2 text-sm"
        >
          <option value="">All roles</option>
          {INVITABLE_ROLES.map((r) => (
            <option key={r} value={r}>
              {r.replace('_', ' ')}
            </option>
          ))}
        </Select>
      </div>

      <MembersTable
        loading={members.isPending}
        error={members.error ? 'Could not load members.' : null}
        members={members.data?.users ?? []}
        hasFilters={hasFilters}
        onResend={handleResend}
        resendPending={resendPending}
        managers={managers}
        managerRoster={managerRoster}
        isAdmin={isAdmin}
        onSetManager={handleSetManager}
        managerPending={managerPending}
        managerErrors={managerErrors}
        onSetRole={handleSetRole}
        rolePending={rolePending}
        roleErrors={roleErrors}
        highlightId={highlightId}
        currentUserId={session.userId}
        onToggleStatus={handleToggleStatus}
        statusPending={statusPending}
        hrSafetyContactUserId={members.data?.hrSafetyContactUserId ?? null}
        pendingHrSafetyContactUserId={members.data?.pendingHrSafetyContactUserId ?? null}
        onNominateSafetyContact={handleNominateSafetyContact}
        onAcceptSafetyContact={handleAcceptSafetyContact}
        onCancelSafetyContact={handleCancelSafetyContact}
        safetyContactPending={safetyContactPending}
      />

      {members.data && members.data.users.length > 0 ? (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {total === 0
              ? 'No members'
              : `${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} of ${total}`}
          </span>
          <div className="flex gap-2">
            <Button variant="secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <Button
              variant="secondary"
              disabled={(page + 1) * PAGE_SIZE >= total}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function InviteForm({
  pending,
  error,
  onSubmit,
}: {
  pending: boolean;
  error: string | null;
  onSubmit: (input: InviteUserRequest) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('Manager');
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof InviteUserRequest, string>>>(
    {},
  );

  function handle(e: FormEvent) {
    e.preventDefault();
    const parsed = InviteUserRequestSchema.safeParse({ name, email, role });
    if (!parsed.success) {
      const next: Partial<Record<keyof InviteUserRequest, string>> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof InviteUserRequest;
        if (!next[key]) next[key] = issue.message;
      }
      setFieldErrors(next);
      return;
    }
    setFieldErrors({});
    onSubmit(parsed.data);
    setName('');
    setEmail('');
    setRole('Manager');
  }

  return (
    <form
      onSubmit={handle}
      className="grid gap-4 rounded-lg border border-border p-6 sm:grid-cols-[1fr_1fr_auto_auto] sm:items-end"
    >
      <Field label="Name" error={fieldErrors.name}>
        {(id, descProps) => (
          <TextInput
            id={id}
            {...descProps}
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        )}
      </Field>
      <Field label="Email" error={fieldErrors.email}>
        {(id, descProps) => (
          <TextInput
            id={id}
            {...descProps}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        )}
      </Field>
      <Field label="Role" error={fieldErrors.role}>
        {(id, descProps) => (
          <Select
            id={id}
            {...descProps}
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
          >
            {INVITABLE_ROLES.map((r) => (
              <option key={r} value={r}>
                {r.replace('_', ' ')}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <div className="flex flex-col">
        <Button type="submit" loading={pending}>
          Send invite
        </Button>
        {error ? (
          <p className="mt-1 text-sm text-traffic-red" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </form>
  );
}

function MembersTable({
  loading,
  error,
  members,
  hasFilters,
  onResend,
  resendPending,
  managers,
  managerRoster,
  isAdmin,
  onSetManager,
  managerPending,
  managerErrors,
  onSetRole,
  rolePending,
  roleErrors,
  highlightId,
  currentUserId,
  onToggleStatus,
  statusPending,
  hrSafetyContactUserId,
  pendingHrSafetyContactUserId,
  onNominateSafetyContact,
  onAcceptSafetyContact,
  onCancelSafetyContact,
  safetyContactPending,
}: {
  loading: boolean;
  error: string | null;
  members: UserSummary[];
  hasFilters: boolean;
  onResend: (m: UserSummary) => void;
  resendPending: Record<string, boolean>;
  managers: UserSummary[];
  managerRoster: UserSummary[];
  isAdmin: boolean;
  onSetManager: (employeeId: string, managerId: string) => void;
  managerPending: Record<string, boolean>;
  managerErrors: Record<string, string | null>;
  onSetRole: (m: UserSummary, role: Role) => void;
  rolePending: Record<string, boolean>;
  roleErrors: Record<string, string | null>;
  highlightId: string | null;
  currentUserId: string;
  onToggleStatus: (m: UserSummary) => void;
  statusPending: Record<string, boolean>;
  hrSafetyContactUserId: string | null;
  pendingHrSafetyContactUserId: string | null;
  onNominateSafetyContact: (m: UserSummary) => void;
  onAcceptSafetyContact: () => void;
  onCancelSafetyContact: () => void;
  safetyContactPending: Record<string, boolean>;
}) {
  // Scrolls to + briefly highlights the row named by `/hr/members?employee=<id>`
  // — the deep link `HrNewCase.tsx` sends HR to when a case can't be created
  // for an unmapped employee. Runs once the row actually exists in the DOM.
  const highlightedRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    if (!highlightId) return;
    highlightedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightId, members.length]);

  if (loading) return <p className="text-muted-foreground">Loading members…</p>;
  if (error) return <p className="text-traffic-red">{error}</p>;
  if (members.length === 0) {
    return (
      <p className="text-muted-foreground">
        {hasFilters
          ? 'No members match the current search or filters.'
          : 'No members yet — start with the form above.'}
      </p>
    );
  }

  return (
    // overflow-x-auto so the Status column isn't clipped on phones.
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-left text-muted-foreground">
          <tr>
            <th className="px-4 py-2 font-medium">Name</th>
            <th className="px-4 py-2 font-medium">Email</th>
            <th className="px-4 py-2 font-medium">Role</th>
            <th className="px-4 py-2 font-medium">Status</th>
            <th className="px-4 py-2 font-medium">Manager</th>
            <th className="px-4 py-2 text-right font-medium">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => {
            // Per-row spinner so multiple resends can be in-flight at once
            // (HR resends for two users back-to-back) without the loading
            // state propagating across the whole table. The button is
            // disabled for users that can't currently be re-armed
            // (Opted_Out / Pending) — see `canResend` for the rationale.
            const pending = resendPending[m.id] ?? false;
            const enabled = canResend(m) && !pending;
            const isHighlighted = m.id === highlightId;
            return (
              <tr
                key={m.id}
                ref={isHighlighted ? highlightedRef : undefined}
                className={
                  'border-t border-border' +
                  (isHighlighted
                    ? ' bg-traffic-amber/10 ring-1 ring-traffic-amber/40 ring-inset'
                    : '')
                }
              >
                <td className="px-4 py-2">{m.name}</td>
                <td className="px-4 py-2 text-muted-foreground">{m.email}</td>
                <td className="px-4 py-2">
                  <RoleCell
                    member={m}
                    isAdmin={isAdmin}
                    isSelf={m.id === currentUserId}
                    pending={rolePending[m.id] ?? false}
                    error={roleErrors[m.id] ?? null}
                    onChange={(role) => onSetRole(m, role)}
                  />
                </td>
                <td className="px-4 py-2 text-muted-foreground">{m.status}</td>
                <td className="px-4 py-2">
                  {m.role === 'Employee' ? (
                    <ManagerCell
                      employee={m}
                      managers={managers}
                      managerRoster={managerRoster}
                      isAdmin={isAdmin}
                      pending={managerPending[m.id] ?? false}
                      error={managerErrors[m.id] ?? null}
                      onChange={(managerId) => onSetManager(m.id, managerId)}
                    />
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="secondary"
                      loading={pending}
                      disabled={!enabled}
                      onClick={() => onResend(m)}
                      aria-label={
                        canResend(m)
                          ? `Resend sign-in link to ${m.name}`
                          : `Cannot resend link to ${m.name} — ${m.status.toLowerCase()}`
                      }
                    >
                      Resend link
                    </Button>
                    {m.role === 'HR_Admin' ? (
                      <SafetyContactAction
                        member={m}
                        isAdmin={isAdmin}
                        isCurrent={m.id === hrSafetyContactUserId}
                        isPending={m.id === pendingHrSafetyContactUserId}
                        isSelf={m.id === currentUserId}
                        nominatePending={safetyContactPending[m.id] ?? false}
                        acceptPending={safetyContactPending.accept ?? false}
                        cancelPending={safetyContactPending.cancel ?? false}
                        onNominate={() => onNominateSafetyContact(m)}
                        onAccept={onAcceptSafetyContact}
                        onCancel={onCancelSafetyContact}
                      />
                    ) : null}
                    <StatusActionButton
                      member={m}
                      isAdmin={isAdmin}
                      isSelf={m.id === currentUserId}
                      pending={statusPending[m.id] ?? false}
                      onToggle={() => onToggleStatus(m)}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Inline manager picker for a single Employee row. Not a <Field> (no
// visible label) — the column header already labels it, so this follows
// the same dense-table convention as the "Resend link" button's
// aria-label rather than a full labelled Field.
function ManagerCell({
  employee,
  managers,
  managerRoster,
  isAdmin,
  pending,
  error,
  onChange,
}: {
  employee: UserSummary;
  managers: UserSummary[];
  managerRoster: UserSummary[];
  isAdmin: boolean;
  pending: boolean;
  error: string | null;
  onChange: (managerId: string) => void;
}) {
  // The employee's currently-assigned manager, resolved from the full
  // manager roster (all statuses, not just Active) — `ManagerSelect` itself
  // decides whether this is needed as a fallback (see ManagerSelect.tsx for
  // the underlying <select> bug this guards against: a deactivated manager
  // is excluded from `managers` (Active-only) but must still resolve here so
  // the select doesn't silently mis-render as the first Active manager).
  const currentManager = employee.managerId
    ? (managerRoster.find((u) => u.id === employee.managerId) ?? null)
    : null;

  return (
    <div className="flex flex-col gap-1">
      <ManagerSelect
        ariaLabel={`Manager for ${employee.name}`}
        value={employee.managerId}
        managers={managers}
        currentManager={currentManager}
        disabled={!isAdmin || pending}
        onChange={onChange}
      />
      {error ? (
        <p className="text-xs text-traffic-red" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

// Inline role editor for a single row. Not a <Field> (no visible label) —
// the column header already labels it, same dense-table convention as
// `ManagerCell`. Disabled for the signed-in HR_Admin's own row (mirrors the
// backend's `cannot_change_self` guard) so there's no confusing mid-session
// self-demotion affordance.
function RoleCell({
  member,
  isAdmin,
  isSelf,
  pending,
  error,
  onChange,
}: {
  member: UserSummary;
  isAdmin: boolean;
  isSelf: boolean;
  pending: boolean;
  error: string | null;
  onChange: (role: Role) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Select
        aria-label={`Role for ${member.name}`}
        value={member.role}
        disabled={!isAdmin || isSelf || pending}
        onChange={(e) => onChange(e.target.value as Role)}
        className="py-1.5 text-sm"
        title={
          isSelf ? 'You cannot change your own role — another HR Admin must do it.' : undefined
        }
      >
        {INVITABLE_ROLES.map((r) => (
          <option key={r} value={r}>
            {r.replace('_', ' ')}
          </option>
        ))}
      </Select>
      {error ? (
        <p className="text-xs text-traffic-red" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

// HR Safety Contact indicator + two-step nominate/accept/cancel, HR_Admin
// rows only — the named individual who receives red-flag crisis
// notifications (DESIGN §3.3.4; Companion mode is disabled org-wide without
// one). DESIGN.md gates a reassignment on "the receiving user completing
// the acknowledgement step" — nominating doesn't move the live contact,
// only the nominee's own Accept does.
function SafetyContactAction({
  member,
  isAdmin,
  isCurrent,
  isPending,
  isSelf,
  nominatePending,
  acceptPending,
  cancelPending,
  onNominate,
  onAccept,
  onCancel,
}: {
  member: UserSummary;
  isAdmin: boolean;
  isCurrent: boolean;
  isPending: boolean;
  isSelf: boolean;
  nominatePending: boolean;
  acceptPending: boolean;
  cancelPending: boolean;
  onNominate: () => void;
  onAccept: () => void;
  onCancel: () => void;
}) {
  if (isCurrent) {
    return (
      <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
        Safety contact
      </span>
    );
  }
  if (isPending) {
    return (
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center rounded-full bg-traffic-amber/10 px-2 py-1 text-xs font-medium text-traffic-amber">
          Pending acceptance
        </span>
        {isSelf ? (
          <Button
            variant="ghost"
            className="px-2 py-1 text-xs"
            loading={acceptPending}
            disabled={!isAdmin || acceptPending}
            onClick={onAccept}
            aria-label="Accept Safety Contact nomination"
          >
            Accept
          </Button>
        ) : (
          <Button
            variant="ghost"
            className="px-2 py-1 text-xs"
            loading={cancelPending}
            disabled={!isAdmin || cancelPending}
            onClick={onCancel}
            aria-label={`Cancel Safety Contact nomination for ${member.name}`}
          >
            Cancel nomination
          </Button>
        )}
      </div>
    );
  }
  // Pending/Opted_Out HR_Admins can't be nominated (the route 400s
  // user_not_eligible) — nothing actionable to surface, so no button
  // rather than a disabled one with a reason (contrast StatusActionButton,
  // which explains its disabled states because there's a real "why" to
  // give; here there's just nothing to do).
  if (member.status !== 'Active') return null;
  return (
    <Button
      variant="ghost"
      className="px-2 py-1 text-xs"
      loading={nominatePending}
      disabled={!isAdmin || nominatePending}
      onClick={onNominate}
      aria-label={`Nominate ${member.name} as the Safety Contact`}
    >
      Nominate as safety contact
    </Button>
  );
}

// Deactivate/Reactivate toggle for a single row. Mirrors AdminAdmins.tsx's
// suspend/reactivate button — same ghost-variant + red-text-for-destructive
// styling, same disabled-with-title treatment for the cases that can't be
// acted on rather than hiding the control outright (Pending invite, or the
// signed-in HR Admin's own row).
function StatusActionButton({
  member,
  isAdmin,
  isSelf,
  pending,
  onToggle,
}: {
  member: UserSummary;
  isAdmin: boolean;
  isSelf: boolean;
  pending: boolean;
  onToggle: () => void;
}) {
  const baseClassName = 'px-2 py-1 text-xs';

  if (member.status === 'Pending') {
    return (
      <Button
        variant="ghost"
        className={`${baseClassName} disabled:text-muted-foreground`}
        disabled
        title="A Pending invite can't be deactivated — resend the invite or wait for it to be accepted."
      >
        Deactivate
      </Button>
    );
  }

  if (member.status === 'Opted_Out') {
    return (
      <Button
        variant="ghost"
        className={baseClassName}
        loading={pending}
        disabled={!isAdmin || pending}
        onClick={onToggle}
        aria-label={`Reactivate ${member.name}`}
      >
        Reactivate
      </Button>
    );
  }

  return (
    <Button
      variant="ghost"
      className={`${baseClassName} text-traffic-red disabled:text-muted-foreground`}
      loading={pending}
      disabled={!isAdmin || isSelf || pending}
      onClick={onToggle}
      aria-label={
        isSelf
          ? `Cannot deactivate ${member.name} — that's your own account`
          : `Deactivate ${member.name}`
      }
      {...(isSelf
        ? { title: 'You cannot deactivate yourself — another HR Admin must do it.' }
        : {})}
    >
      Deactivate
    </Button>
  );
}

function errorMessage(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    if (err.status === 409) return 'That email is already invited to this organisation.';
    if (err.status === 400) return 'Please check the form and try again.';
    return 'Something went wrong sending the invite.';
  }
  return 'Network error.';
}

// Resend-specific error translation. Routes the route's status code to
// a humanised message; surfaces the 429 cooldown's `retryAfterMs` as
// a seconds-until-retry hint so HR knows when to try again. The
// `resend_cooldown` shape comes from routes/users.ts:resend-invite.
function translateResendError(err: unknown): {
  kind: 'err';
  message: string;
  retryAfterMs?: number;
} {
  if (!(err instanceof ApiError)) return { kind: 'err', message: 'Could not resend the invite.' };
  // The route returns `{ error: 'resend_cooldown', retryAfterMs }` for
  // the 429 case and `{ error: 'user_opted_out' }` for the 409 case.
  // `err.body` is typed as `Record<string, unknown> | null` so we
  // narrow the fields we read with string checks.
  const body = err.body ?? {};
  if (err.status === 429 && body.error === 'resend_cooldown') {
    const retryAfterMs = typeof body.retryAfterMs === 'number' ? body.retryAfterMs : 0;
    const retrySecs = Math.ceil(retryAfterMs / 1000);
    return {
      kind: 'err',
      message: `Please wait ${retrySecs}s before resending another link.`,
      retryAfterMs,
    };
  }
  if (err.status === 409 && body?.error === 'user_opted_out') {
    return {
      kind: 'err',
      message: 'This user has opted out — reactivate them in Members first.',
    };
  }
  if (err.status === 404) {
    return { kind: 'err', message: 'User not found in your organisation.' };
  }
  if (err.status === 403) {
    return { kind: 'err', message: 'You don’t have permission to resend this invite.' };
  }
  return { kind: 'err', message: 'Could not resend the invite. Try again in a moment.' };
}

// Error translation for the inline manager picker. The route
// (routes/users.ts PUT /:id/manager) returns distinct 404 codes for the
// employee vs the manager side of the request so this can stay precise
// rather than falling back to a generic "not found".
function translateManagerError(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Could not update the manager. Try again.';
  const body = err.body ?? {};
  if (err.status === 404 && body.error === 'manager_not_found') {
    return 'That manager is no longer active — pick another.';
  }
  if (err.status === 404 && body.error === 'employee_not_found') {
    return 'This employee could not be found — refresh and try again.';
  }
  if (err.status === 403) {
    return 'You don’t have permission to change this.';
  }
  return 'Could not update the manager. Try again.';
}

// Error translation for the deactivate/reactivate toggle. The routes
// (routes/users.ts POST /:id/deactivate, /:id/reactivate) return distinct
// codes for each guard so this can give HR the actual reason rather than a
// generic failure.
function translateStatusError(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Could not update this person’s status. Try again.';
  const body = err.body ?? {};
  const code = typeof body.error === 'string' ? body.error : null;
  if (code === 'cannot_deactivate_self') return 'You cannot deactivate yourself.';
  if (code === 'is_safety_contact') {
    return 'This person is the org’s HR Safety Contact — reassign that role first.';
  }
  if (code === 'last_active_hr_admin') {
    return 'This is the only active HR Admin in your org — invite another before deactivating this one.';
  }
  if (code === 'already_opted_out') return 'This person is already deactivated.';
  if (code === 'not_opted_out') return 'This person is already active.';
  if (err.status === 404) return 'This person could not be found — refresh and try again.';
  if (err.status === 403) return 'You don’t have permission to change this.';
  return 'Could not update this person’s status. Try again.';
}

// Error translation for the role-change control (routes/users.ts POST
// /:id/role). The route returns distinct codes for each guard so this can
// give HR the actual reason rather than a generic failure.
function translateRoleError(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Could not update this person’s role. Try again.';
  const body = err.body ?? {};
  const code = typeof body.error === 'string' ? body.error : null;
  if (code === 'same_role') return 'They already have that role.';
  if (code === 'cannot_change_self') {
    return 'You cannot change your own role — another HR Admin must do it.';
  }
  if (code === 'is_safety_contact') {
    return 'This person is the org’s HR Safety Contact — reassign that role first.';
  }
  if (code === 'last_active_hr_admin') {
    return 'This is the only active HR Admin in your org — invite another before changing this one’s role.';
  }
  if (code === 'user_opted_out') return 'This person is deactivated — reactivate them first.';
  if (err.status === 404) return 'This person could not be found — refresh and try again.';
  if (err.status === 403) return 'You don’t have permission to change this.';
  return 'Could not update this person’s role. Try again.';
}

// Error translation for the Safety Contact nominate/accept/cancel routes
// (routes/users.ts POST /:id/safety-contact, /safety-contact/accept,
// /safety-contact/cancel).
function translateSafetyContactError(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Could not update the Safety Contact. Try again.';
  const body = err.body ?? {};
  const code = typeof body.error === 'string' ? body.error : null;
  if (code === 'user_not_eligible') {
    return 'That person isn’t eligible — only an active HR Admin can be the Safety Contact.';
  }
  if (code === 'no_change')
    return 'That person is already the Safety Contact or already nominated.';
  if (code === 'no_pending_nomination') {
    return 'There’s no pending nomination anymore — refresh and try again.';
  }
  if (code === 'not_the_nominee') {
    return 'You’re not the nominee for the pending nomination — refresh and try again.';
  }
  if (code === 'user_not_active') {
    return 'You’re not currently active, so you can’t accept this nomination.';
  }
  if (err.status === 403) return 'You don’t have permission to change this.';
  return 'Could not update the Safety Contact. Try again.';
}

// Error banner body for the resend flow. Extracted so the parent
// component stays JSX-light and the per-status code mapping is
// self-contained.
function ResendErrorMessage({
  banner,
}: {
  banner: { kind: 'err'; message: string; retryAfterMs?: number };
}) {
  return <span>{banner.message}</span>;
}

// Per-row resend affordance: enable when the user is active. Opted_Out
// users are 409'd by the route, and HR needs to reactivate them (the
// StatusActionButton toggle) first. Disabled at the table level gives a
// faster, clearer signal than waiting for the round-trip. Pending users
// are also a no-op (magic link arming for a Pending user doesn't make
// sense — the onboarding flow handles them).
function canResend(m: UserSummary): boolean {
  return m.status === 'Active';
}
